import React, { useState, useEffect, useCallback } from "react";
import { botAnalyticsAPI } from "../services/api";
import { Shield, RefreshCw, TrendingUp, TrendingDown, AlertTriangle, CheckCircle, XCircle, Clock, Zap, Activity } from "lucide-react";

const HEALTH_STYLES = {
  GOOD: { bg: "bg-emerald-500/10 border-emerald-500/30", text: "text-emerald-400", icon: <CheckCircle className="w-4 h-4" /> },
  OK: { bg: "bg-cyan-500/10 border-cyan-500/30", text: "text-cyan-400", icon: <Activity className="w-4 h-4" /> },
  WARNING: { bg: "bg-amber-500/10 border-amber-500/30", text: "text-amber-400", icon: <AlertTriangle className="w-4 h-4" /> },
  CRITICAL: { bg: "bg-red-500/10 border-red-500/30", text: "text-red-400", icon: <XCircle className="w-4 h-4" /> },
  PENDING: { bg: "bg-slate-500/10 border-slate-500/30", text: "text-slate-400", icon: <Clock className="w-4 h-4" /> },
};

const SEVERITY_COLORS = {
  CRITICAL: "text-red-400 bg-red-500/10 border-red-500/30",
  HIGH: "text-orange-400 bg-orange-500/10 border-orange-500/30",
  MEDIUM: "text-amber-400 bg-amber-500/10 border-amber-500/30",
  LOW: "text-slate-400 bg-slate-500/10 border-slate-500/30",
};

export default function BotAutopilot() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [running, setRunning] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try { const res = await botAnalyticsAPI.getAutopilot(); setData(res); } catch (e) {}
    setLoading(false);
  }, []);

  const triggerRun = async () => {
    setRunning(true);
    try { const res = await botAnalyticsAPI.triggerAutopilot(); setData(res); } catch (e) {}
    setRunning(false);
  };

  useEffect(() => { fetchData(); const iv = setInterval(fetchData, 120000); return () => clearInterval(iv); }, [fetchData]);

  const health = HEALTH_STYLES[data?.health] || HEALTH_STYLES.PENDING;
  const wr20 = data?.win_rate_20 || 0;
  const wr50 = data?.win_rate_50 || 0;
  const errors = data?.errors_detected || [];
  const adjustments = data?.adjustments_made || [];
  const updatedAt = data?.timestamp ? new Date(data.timestamp).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" }) : "--:--";

  return (
    <div className={`glass-card rounded-xl p-4 border ${health.bg} space-y-3`} data-testid="autopilot-panel">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className={`w-6 h-6 rounded-full flex items-center justify-center ${health.bg}`}>
            <Shield className={`w-3.5 h-3.5 ${health.text}`} />
          </div>
          <h3 className="text-sm font-black text-white uppercase tracking-wider">Autopilot</h3>
          <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded border ${health.bg} ${health.text}`}>
            {data?.health || "PENDING"}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-[10px] text-slate-500 flex items-center gap-1"><Clock className="w-2.5 h-2.5" />{updatedAt}</span>
          <button onClick={triggerRun} disabled={running} className="px-2 py-1 rounded bg-cyan-500/15 border border-cyan-500/30 text-cyan-400 text-[10px] font-bold hover:bg-cyan-500/25 disabled:opacity-40 flex items-center gap-1" data-testid="run-autopilot">
            {running ? <RefreshCw className="w-2.5 h-2.5 animate-spin" /> : <Zap className="w-2.5 h-2.5" />}
            {running ? "Analyse..." : "Analyser"}
          </button>
        </div>
      </div>

      {/* KPIs */}
      {data && data.health !== "PENDING" && (
        <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
          <KPI label="WR 20" value={`${wr20}%`} color={wr20 >= 50 ? "emerald" : wr20 >= 40 ? "amber" : "red"} />
          <KPI label="WR 50" value={`${wr50}%`} color={wr50 >= 50 ? "emerald" : wr50 >= 40 ? "amber" : "red"} />
          <KPI label="Avg Win" value={`${data.avg_win_pct > 0 ? "+" : ""}${data.avg_win_pct || 0}%`} color="emerald" />
          <KPI label="Avg Loss" value={`${data.avg_loss_pct || 0}%`} color="red" />
          <KPI label="Hold W" value={`${data.avg_hold_winners_min || 0}m`} color="cyan" />
          <KPI label="Hold L" value={`${data.avg_hold_losers_min || 0}m`} color="slate" />
        </div>
      )}

      {/* R:R Actual */}
      {data?.actual_rr !== undefined && (
        <div className="flex items-center gap-2">
          <span className="text-[10px] text-slate-500">R:R Reel:</span>
          <span className={`text-xs font-bold ${data.actual_rr >= 1.0 ? "text-emerald-400" : data.actual_rr >= 0.7 ? "text-amber-400" : "text-red-400"}`}>
            {data.actual_rr}:1
          </span>
          {data.actual_rr < 1.0 && <span className="text-[9px] text-red-400/70">Les pertes &gt; les gains</span>}
        </div>
      )}

      {/* Top Winners / Losers */}
      {data?.top_winners?.length > 0 && (
        <div className="flex gap-3">
          <div className="flex-1">
            <span className="text-[10px] text-emerald-400 font-bold flex items-center gap-1 mb-1"><TrendingUp className="w-2.5 h-2.5" />Top Wins</span>
            {data.top_winners.slice(0, 3).map((w, i) => (
              <div key={i} className="text-[10px] text-slate-300">{w.ticker} <span className="text-emerald-400">+{w.pnl}%</span></div>
            ))}
          </div>
          <div className="flex-1">
            <span className="text-[10px] text-red-400 font-bold flex items-center gap-1 mb-1"><TrendingDown className="w-2.5 h-2.5" />Top Losses</span>
            {(data.top_losers || []).slice(0, 3).map((l, i) => (
              <div key={i} className="text-[10px] text-slate-300">{l.ticker} <span className="text-red-400">{l.pnl}%</span></div>
            ))}
          </div>
        </div>
      )}

      {/* Errors */}
      {errors.length > 0 && (
        <div className="space-y-1">
          <span className="text-[10px] text-amber-400 font-bold uppercase">Erreurs detectees ({errors.length})</span>
          {errors.map((e, i) => (
            <div key={i} className={`rounded-lg px-2 py-1.5 border text-[10px] ${SEVERITY_COLORS[e.severity] || SEVERITY_COLORS.LOW}`} data-testid={`error-${e.type}`}>
              <div className="font-bold">{e.type} <span className="opacity-60">({e.severity})</span></div>
              <div className="opacity-80">{e.detail}</div>
              {e.fix && <div className="text-[9px] opacity-60 mt-0.5">Fix: {e.fix}</div>}
            </div>
          ))}
        </div>
      )}

      {/* Auto-Adjustments */}
      {adjustments.length > 0 && (
        <div className="space-y-1">
          <span className="text-[10px] text-cyan-400 font-bold uppercase flex items-center gap-1"><Zap className="w-2.5 h-2.5" />Auto-corrections ({adjustments.length})</span>
          {adjustments.map((a, i) => (
            <div key={i} className="bg-cyan-500/5 border border-cyan-500/20 rounded px-2 py-1 text-[10px] text-cyan-300">{a}</div>
          ))}
        </div>
      )}

      {!data && !loading && (
        <div className="text-center py-4 text-slate-500 text-xs">
          Cliquez "Analyser" pour lancer l'autopilot
        </div>
      )}
    </div>
  );
}

function KPI({ label, value, color }) {
  const colors = {
    emerald: "text-emerald-400 bg-emerald-500/10",
    amber: "text-amber-400 bg-amber-500/10",
    red: "text-red-400 bg-red-500/10",
    cyan: "text-cyan-400 bg-cyan-500/10",
    slate: "text-slate-400 bg-slate-500/10",
  };
  return (
    <div className={`rounded-lg px-2 py-1.5 ${colors[color] || colors.slate} text-center`}>
      <div className={`text-xs font-bold ${colors[color]?.split(" ")[0]}`}>{value}</div>
      <div className="text-[9px] text-slate-500">{label}</div>
    </div>
  );
}
